import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import * as Label from "@radix-ui/react-label";
import { authEndpoint } from "~/constants";

type ResetProps = {
  className?: string;
  title?: string;
  onSuccess?: Function;
  onFailure?: Function;
  onSignUp?: Function;
  onSignIn?: Function;
};

export function Reset({
  className = "",
  title = "Reset your password.",
  onSignUp = null,
  onSignIn = null,
}: ResetProps) {
  const router = useRouter();

  const [state, setState] = useState({
    token: router.query["token"],
    error: null,
    success: false,
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const body = JSON.stringify({
      email: form.email?.value.toLowerCase(),
      password: form.password?.value,
      token: form.resetToken?.value,
    });
    try {
      let resp = await fetch(`${authEndpoint}/reset`, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        body,
      });
      if (resp.ok) {
        setState((state) => ({ ...state, success: true, error: null }));
      } else {
        resp
          .text()
          .then((error) =>
            setState((state) => ({ ...state, success: false, error })),
          );
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        success: false,
        error: "Unable to connect to server.",
      }));
    }
  };

  const onTokenChange = (event: React.FormEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    setState((state) => ({ ...state, token: input.value }));
    router.replace(`/reset?token=${input.value}`);
  };

  if (state.success) {
    if (router.query.token == null) {
      return (
        <div className={`w-[400px] max-w-full p-6 m-auto ${className}`}>
          <h2 className="font-bold text-2xl mb-4">Check your email.</h2>
          <Link
            href="/reset?token="
            className="text-black visited:text-black underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 rounded-sm"
          >
            Enter reset token manually
          </Link>
        </div>
      );
    } else {
      return (
        <div className={`w-[400px] max-w-full p-6 m-auto ${className}`}>
          <h2 className="font-bold text-2xl mb-4">
            Password has been successfully reset!
          </h2>
          <Link
            href="/login"
            className="text-black visited:text-black underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 rounded-sm"
          >
            Sign in
          </Link>
        </div>
      );
    }
  }

  return (
    <form
      className={`w-[400px] max-w-full p-6 m-auto ${className}`}
      onSubmit={onSubmit}
    >
      <h2 className="font-bold text-2xl mb-4">{title}</h2>
      {state.error && (
        <div className="my-3 p-4 rounded font-medium text-sm text-red-500 bg-red-400/10 ring-2 ring-red-400 ring-inset">
          {state.error}
        </div>
      )}
      {router.query.token == null ? (
        <div className="my-2">
          <Label.Root
            className="block pt-1.5 pb-0.5 select-none uppercase font-semibold text-xs text-gray-400"
            htmlFor="email"
          >
            Email Address
          </Label.Root>
          <input
            className="my-1 px-3 h-10 w-full bg-gray-100 transition duration-[25ms] rounded ring-gray-200 border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-sky-300 focus:border-sky-300 hover:bg-gray-200 hover:bg-opacity-75 focus:hover:bg-gray-100"
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
          />
        </div>
      ) : (
        <>
          <div className="my-2">
            <Label.Root
              className="block pt-1.5 pb-0.5 select-none uppercase font-semibold text-xs text-gray-400"
              htmlFor="password"
            >
              New Password
            </Label.Root>
            <input
              className="my-1 px-3 h-10 w-full bg-gray-100 transition duration-[25ms] rounded ring-gray-200 border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-sky-300 focus:border-sky-300 hover:bg-gray-200 hover:bg-opacity-75 focus:hover:bg-gray-100"
              id="password"
              type="password"
              name="password"
              autoComplete="new-password"
              autoFocus
              required
            />
          </div>
          <div className="my-2">
            <Label.Root
              className="block pt-1.5 pb-0.5 select-none uppercase font-semibold text-xs text-gray-400"
              htmlFor="token"
            >
              Reset Token
            </Label.Root>
            <input
              className="my-1 px-3 h-10 w-full bg-gray-100 transition duration-[25ms] rounded ring-gray-200 border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-sky-300 focus:border-sky-300 hover:bg-gray-200 hover:bg-opacity-75 focus:hover:bg-gray-100"
              id="token"
              type="text"
              name="resetToken"
              value={state.token}
              onChange={onTokenChange}
              required
            />
          </div>
        </>
      )}
      <div className="mb-2 mt-4">
        <button
          className="px-3 h-10 rounded font-medium text-xs my-1 transition duration-[25ms] leading-3 w-full text-sky-400 bg-sky-400 bg-opacity-10 focus:bg-opacity-20 hover:bg-opacity-20 active:bg-opacity-30 border-2 border-sky-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-sky-300 select-none"
          type="submit"
          aria-label="submit"
        >
          Continue
        </button>
      </div>
      <div className="my-2">
        <div className="text-xs leading-snug text-gray-400">
          Need an account?{" "}
          <Link
            href="/signup"
            className="text-black visited:text-black underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 rounded-sm"
          >
            Register.
          </Link>
        </div>
        <div className="text-xs leading-snug text-gray-400">
          Already have an account?{" "}
          <Link
            href="/login"
            className="text-black visited:text-black underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 rounded-sm"
          >
            Sign in.
          </Link>
        </div>
      </div>
    </form>
  );
}

export default Reset;
